import React from 'react'
import { SurveyQuestionType } from '../../../../models/Survey'
import { type ReportWithComments, type Report, type KeyStatSurveyItem, type ReportsV2 } from '../../../../models/Report'
import { type SurveyCompletionRate } from '../../../../models/Division'
import { handleSurveyItems, titleFormatted } from '../../../../helpers'
import SurveyItemResponsePlaceHolder from '../SurveyItemResponsePlaceHolder'
import CommentCard from '../CommentCard'
import SurveyReportChart from './SurveyReportChart'
import { useReportsContext } from '../../ReportsContext'

interface Props {
  reports: ReportsV2
  completionRate: SurveyCompletionRate
  reportError: Record<string, any> | null
}

const SurveyReportItems: React.FC<Props> = ({ reports, completionRate, reportError }) => {
  const { selectedTrend } = useReportsContext()
  const settings = localStorage.getItem('displaySettings')
  const parsedSettings = settings ? JSON.parse(settings) : null

  const isGenericAcademicUnit = completionRate && 'divisionId' in completionRate

  let questionNumber = 0

  // Render for FreeResponse question type
  const renderFreeResponse = (report: ReportWithComments): JSX.Element => {
    const titleText = `${questionNumber + 1}. ${report.prompt}`
    questionNumber += 1
    if ('releaseDate' in report || isGenericAcademicUnit) {
      return <SurveyItemResponsePlaceHolder key={questionNumber} title={titleText} releaseTimeStamp={report?.releaseDate} />
    }
    return <CommentCard key={questionNumber} title={titleText} comments={report.comments} />
  }
  const showSlice = parsedSettings?.slices?.length > 1 && parsedSettings?.comparisons?.[0].comparison === null
  // Render for LinearScale, MultipleChoice, MultiSelect
  const renderBarChart = (report: any, surveyItem: KeyStatSurveyItem | null = null): JSX.Element => {
    questionNumber += 1
    return (
      <SurveyReportChart
        report={report}
        surveyItem={surveyItem}
        reports={reports}
        selectedTrend={selectedTrend}
        showSlice={showSlice}
        parsedSettings={parsedSettings}
        questionNumber={questionNumber - 1}
      />
    )
  }

  // Render for Title question type
  const renderTitle = (report: Report): JSX.Element => titleFormatted(report.prompt)

  const handleReportItem = (report: Report, surveyItem: KeyStatSurveyItem | null = null): JSX.Element => {
    switch (report.itemType) {
      case SurveyQuestionType.FreeResponse:
        return renderFreeResponse(report)
      case SurveyQuestionType.LinearScale:
      case SurveyQuestionType.MultipleChoice:
      case SurveyQuestionType.MultiSelect:
        return renderBarChart(report, surveyItem)
      case SurveyQuestionType.Title:
        return renderTitle(report)
      default:
        return <></>
    }
  }

  return (
    <>
      {
        handleSurveyItems(reports.surveySections, handleReportItem)
      }
    </>
  )
}

export default SurveyReportItems
